'use client';

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import theme from '../../theme';

export const ViewportContext = React.createContext({ current: null, up: () => {}, down: () => {}, between: () => {} });

/**
 *
 * @type {import('.').ContextType}
 */
const ViewportProvider = ({ children, ssrViewport }) => {
  const [current, setCurrent] = useState(ssrViewport);

  const breakpoints = theme.sizes.screens;

  let up = useCallback(
    (breakpoint) => {
      const breakpointValue = breakpoints[breakpoint] || 0;
      return window.matchMedia(`(min-width: ${breakpointValue})`).matches;
    },
    [breakpoints],
  );
  let down = useCallback(
    (breakpoint) => {
      const breakpointValue = breakpoints[breakpoint] || 0;
      return window.matchMedia(`(max-width: ${breakpointValue})`).matches;
    },
    [breakpoints],
  );

  useEffect(() => {
    const updateCurrent = () => {
      const sortedBreakpoints = Object.entries(breakpoints).sort(([, v1], [, v2]) => {
        return parseInt(v2, 10) - parseInt(v1, 10);
      });

      setCurrent(
        (sortedBreakpoints.find(([key]) => {
          return up(key);
        }) || ['xs'])[0],
      );
    };
    updateCurrent();
    window.addEventListener('resize', updateCurrent);
    return () => {
      window.removeEventListener('resize', updateCurrent);
    };
  }, [breakpoints, up]);

  if (typeof window === 'undefined' || !window.matchMedia) {
    const currentViewport = parseInt(breakpoints[ssrViewport], 10) || 0;
    up = (breakpoint) => {
      return parseInt(breakpoints[breakpoint] || 0, 10) < currentViewport;
    };
    down = (breakpoint) => {
      return parseInt(breakpoints[breakpoint] || 0, 10) > currentViewport;
    };
  }

  const value = useMemo(() => {
    return {
      current,
      up,
      down,
      between: (breakpoint1, breakpoint2) => {
        return up(breakpoint1) && down(breakpoint2);
      },
    };
  }, [current, up, down]);

  return <ViewportContext.Provider value={value}>{children}</ViewportContext.Provider>;
};

export default ViewportProvider;
